import { IsArray, IsBoolean, IsEnum, IsNumber, IsNumberString, IsOptional, IsString, Max, Min, ValidateNested } from 'class-validator';
import { Type } from 'class-transformer';
import { LayerDto, LayerType } from './layer.dto';
import { String } from '../../../_core/utils/utils.string';
import { hasValidSubjectPosition, validateSubjectPositionBounds, convertSubjectPositionToFloats } from '../../../asset/utils/subject-position.utils';
import { CacheBusterUtils } from '../../../_core/utils/utils.cache-buster';

export enum ResizeMode {
	AUTO = 'auto',
	SCALE = 'scale',
	FIT = 'fit',
	FILL = 'fill',
	LIMIT = 'limit',
	CROP = 'crop',
	THUMB = 'thumb',
	MFIT = 'mfit',
	PAD = 'pad',
	LPAD = 'lpad',
	MPAD = 'mpad',
	LFILL = 'lfill',
	FILL_PAD = 'fill_pad'
}

export enum Gravity {
	AUTO = 'auto',
	NORTH_EAST = 'north_east',
	NORTH = 'north',
	NORTH_WEST = 'north_west',
	WEST = 'west',
	SOUTH_WEST = 'south_west',
	SOUTH = 'south',
	SOUTH_EAST = 'south_east',
	EAST = 'east',
	CENTER = 'center',
	XY_CENTER = 'xy_center'
}

export enum ZoomGravity {
	NORTH_EAST = 'north_east',
	NORTH = 'north',
	NORTH_WEST = 'north_west',
	WEST = 'west',
	SOUTH_WEST = 'south_west',
	SOUTH = 'south',
	SOUTH_EAST = 'south_east',
	EAST = 'east',
	CENTER = 'center'
}

export class LayerOptimizationResizeDto {
	@IsOptional()
	@IsEnum(ResizeMode)
	mode: ResizeMode;

	@IsOptional()
	@IsString()
	width: string;

	@IsOptional()
	@IsString()
	height: string;

	@IsOptional()
	@IsEnum(Gravity)
	gravity: Gravity;

	@IsOptional()
	@IsNumberString()
	x: string;

	@IsOptional()
	@IsNumberString()
	y: string;

	@IsOptional()
	@IsNumberString()
	zoom: string;

	@IsOptional()
	@IsString()
	zoomGravity: ZoomGravity;

	@IsOptional()
	@IsString()
	maxResolution?: string;
}

export class LayerOptimizationTargetCropDto {
	@IsOptional()
	@IsBoolean()
	autoTargetCropEnabled?: boolean; // Auto mode uses focal areas, manual uses tx/ty/tw/th

	@IsOptional()
	tx: string; // Focal Area Box X

	@IsOptional()
	ty: string; // Focal Area Box Y

	@IsOptional()
	tw: string; // Focal Area Box Width

	@IsOptional()
	th: string; // Focal Area Box Height

	@IsOptional()
	@IsString()
	margin: string; // Focal Area Box Margin

	@IsOptional()
	@IsString()
	width: string;

	@IsOptional()
	@IsString()
	height: string;

	@IsOptional()
	@IsString()
	cropMode: 'cover' | 'contain';

	@IsOptional()
	@IsString()
	aspectPriority?: 'crop' | 'target'; // How to prioritize aspect ratio vs. target area (default: 'target')
}

export class LayerOptimizationETrimDto {
	@IsOptional()
	@IsBoolean()
	enabled: boolean;

	@IsOptional()
	@IsNumber()
	@Min(0)
	@Max(100)
	tolerance: number;
}

export class LayerOptimizationSubjectPositionDto {
	@IsOptional()
	@IsNumber()
	sx?: number; // Subject position X coordinate (percentage: 0-100)

	@IsOptional()
	@IsNumber()
	sy?: number; // Subject position Y coordinate (percentage: 0-100)

	@IsOptional()
	@IsNumber()
	sw?: number; // Subject position width (percentage: 0-100)

	@IsOptional()
	@IsNumber()
	sh?: number; // Subject position height (percentage: 0-100)
}

export class LayerOptimizationsDto {
	@IsOptional()
	@IsString()
	provider?: 'cloudinary' | 'tasker';

	@IsOptional()
	@IsString()
	quality?: string;

	@IsOptional()
	videoTrimming?: {
		startOffset?: number;
		endOffset?: number;
		duration?: number;
	};

	@IsOptional()
	@IsString()
	baseline?: string; // https://cloudinary.com/documentation/transformation_reference#bl_baseline

	@IsOptional()
	@ValidateNested()
	@Type(() => LayerOptimizationResizeDto)
	resize?: LayerOptimizationResizeDto;

	@IsOptional()
	@ValidateNested()
	@Type(() => LayerOptimizationTargetCropDto)
	targetCrop?: LayerOptimizationTargetCropDto;

	@IsOptional()
	@IsArray()
	@IsString({ each: true })
	targetFilterTags?: string[];

	@IsOptional()
	@IsString()
	advanced?: string;

	@IsOptional()
	@IsBoolean()
	advancedOptimizationsEnabled?: boolean;

	@IsOptional()
	@ValidateNested()
	@Type(() => LayerOptimizationETrimDto)
	eTrim: LayerOptimizationETrimDto;


	@IsOptional()
	@ValidateNested()
	@Type(() => LayerOptimizationSubjectPositionDto)
	subjectPosition?: LayerOptimizationSubjectPositionDto;

	/**
	 * Builds a c_subpos transformation string for combining subject position, target crop, and resize.
	 * This method constructs the transformation URL parameters for Taskr's c_subpos operation.
	 *
	 * @param subjectPosition - Subject position bounds (sx, sy, sw, sh) as percentages (0-100)
	 * @param targetCrop - Target crop configuration including tx/ty/tw/th and settings
	 * @param resizeConfig - Resize configuration with width/height
	 * @param isAutoMode - Whether to use automatic focal area detection (target_auto)
	 * @param hasManualValues - Whether manual tx/ty/tw/th coordinates are provided
	 * @param targetFilterSegment - Filter tags string for focal area selection
	 * @returns The c_subpos transformation string
	 */
	private static buildSubposTransformation(
		subjectPosition: LayerOptimizationSubjectPositionDto,
		targetCrop: LayerOptimizationTargetCropDto,
		resizeConfig: LayerOptimizationResizeDto,
		isAutoMode: boolean,
		hasManualValues: boolean,
		targetFilterSegment: string
	): string {
		// Convert subject position from 0-100 percentages to 0.0-1.0 floats
		const subjectFloats = convertSubjectPositionToFloats(subjectPosition);

		// Start building the c_subpos transformation
		let transformation = 'c_subpos';

		// Add subject position parameters (always required for c_subpos)
		transformation += `,sx_${subjectFloats.sx},sy_${subjectFloats.sy},sw_${subjectFloats.sw},sh_${subjectFloats.sh}`;

		// Handle target coordinates based on mode
		if (isAutoMode && !hasManualValues) {
			// Automatic mode: use target_auto placeholder for API to inject focal area
			transformation += `,target_auto${targetFilterSegment}`;
		} else if (hasManualValues) {
			// Manual mode: use provided tx/ty/tw/th coordinates
			transformation += `,tx_${targetCrop.tx},ty_${targetCrop.ty},tw_${targetCrop.tw},th_${targetCrop.th}`;
		}

		// Add width and height (required for c_subpos)
		let width = Math.ceil(Number(resizeConfig.width || 0));
		let height = Math.ceil(Number(resizeConfig.height || 0));

		// Apply max resolution if specified
		if (resizeConfig.maxResolution) {
			let maxResolution = Number(resizeConfig.maxResolution);
			let scale = Math.min(maxResolution / width, maxResolution / height);
			if (scale < 1) {
				width = Math.ceil(width * scale);
				height = Math.ceil(height * scale);
			}
		}

		if (width) {
			transformation += `,w_${width}`;
		} else {
			transformation += `,w_$element.width`;
		}

		if (height) {
			transformation += `,h_${height}`;
		} else {
			transformation += `,h_$element.height`;
		}

		// Note: ar_ (aspect ratio) is NOT needed when w and h are provided
		// Taskr will use the provided w/h dimensions directly

		// Add aspect mode (am_contain or am_cover)
		// if (targetCrop.cropMode === 'contain') {
			transformation += ',am_contain';
		// } else {
			// transformation += ',am_cover';
		// }

		// Add resize flag (defaults to true)
		transformation += ',r_true';

		return transformation;
	}

	public static optimizeLayer(layer: LayerDto, bucketId?: string, taskerEngineUrl?: string, apiUrl?: string, seed?: number) {
		let assetPath = layer[layer.type]?.assetPath || null;

		return {
			...layer,
			[layer.type]: {
				...layer[layer.type],
				assetPath: this.optimizeAsset(assetPath, layer.type, layer.optimizations, bucketId, taskerEngineUrl, apiUrl, seed)
			}
		};
	}

	public static optimizeAsset(
		assetPath: string,
		type: LayerType,
		optimizations: LayerOptimizationsDto,
		bucketId?: string,
		taskerEngineUrl?: string,
		apiUrl?: string,
		seed?: number
	) {
		let resizeConfig = optimizations?.resize;
		let qualityOptimization = optimizations?.quality ? `${optimizations?.quality}/` : '';
		let eTrimOptimization = optimizations?.eTrim;
		let zoomOptimization = '';
		let targetCropOptimization = '';
		const filterTags = (optimizations?.targetFilterTags || [])
			.map(tag => (typeof tag === 'string' ? tag.trim() : ''))
			.filter(tag => !!tag);
		const targetFilterSegment = filterTags.length > 0 ? `,target_filter=${filterTags.join('|')}` : '';
		let transformations = [];

		// Determine the provider automatically if not set
		const hasTargetCrop = !!optimizations?.targetCrop && Object.keys(optimizations?.targetCrop)?.length > 0;
		let provider = optimizations?.provider;

		if (!provider) {
			// Default to cloudinary unless target crop is enabled, which requires tasker
			provider = hasTargetCrop ? 'tasker' : 'cloudinary';
		}

		// If we don't have a target crop, optimizations, or resize mode, return the assetPath
		// if (!hasTargetCrop && !qualityOptimization && !resizeConfig.mode) {
		// 	return assetPath;
		// }

		// If target crop is enabled, ignore gravity as it conflicts with tasker
		// Also default resize mode to 'fill' if not set
		if (hasTargetCrop) {
			resizeConfig = {
				...resizeConfig,
				gravity: resizeConfig?.gravity ? undefined : resizeConfig?.gravity,
				mode: resizeConfig?.mode || ResizeMode.FILL
			};
		}

		// Check if we'll be using c_subpos (will be determined later when processing target crop)
		const hasSubjectPosition = hasValidSubjectPosition(optimizations?.subjectPosition);
		const willUseSubjectPositioning = hasSubjectPosition && validateSubjectPositionBounds(optimizations.subjectPosition).length === 0;

		// Apply transformations. These are same for video and image
		// Skip resize transformations if using c_subpos (they're included in c_subpos itself)
		if (resizeConfig?.mode && !willUseSubjectPositioning) {
			// If either width or height is more than 2000px, let's resize the largest side to 2000px
			// and keep the aspect ratio.
			let width = Math.ceil(Number(resizeConfig.width || 0));
			let height = Math.ceil(Number(resizeConfig.height || 0));

			if (resizeConfig.maxResolution) {
				let maxResolution = Number(resizeConfig.maxResolution);

				// Determine the scale factor needed to resize the larger dimension to maxResolution
				let scale = Math.min(maxResolution / width, maxResolution / height);

				// Apply the scale to both dimensions, but only if scale is less than 1 (i.e., a reduction is needed)
				if (scale < 1) {
					width = Math.ceil(width * scale);
					height = Math.ceil(height * scale);
				}
			}

			if (width) {
				// Make sure there are no decimals in the width.  Let's round up always.
				transformations.push(`w_${width}`);
			} else {
				// If width is not provided, use element width
				transformations.push(`w_$element.width`);
			}

			if (height) {
				// Make sure there are no decimals in the height.  Let's round up always.
				transformations.push(`h_${height}`);
			} else {
				// If width is not provided, use element width
				transformations.push(`h_$element.height`);
			}

			if (resizeConfig?.mode) {
				transformations.push(`c_${resizeConfig.mode}`);
			}

			if (resizeConfig?.gravity) {
				transformations.push(`g_${resizeConfig.gravity}`);
			}

			const isResizeModeLimit = resizeConfig?.mode != null && resizeConfig?.mode === ResizeMode.LIMIT;

			if (provider === 'tasker') {
				transformations.push(`fl_progressive`);

				if(!isResizeModeLimit)
					transformations.push(`we_true`);
			}
		}

		if (type === 'video') {
			if (optimizations.videoTrimming) {
				let { startOffset, endOffset, duration } = optimizations.videoTrimming;
				if (startOffset) {
					transformations.push(`so_${startOffset}`);
				}
				if (endOffset) {
					transformations.push(`eo_${endOffset}`);
				}
				if (duration) {
					transformations.push(`du_${duration}`);
				}
			}
		}

		if (type === 'image') {
			if (resizeConfig?.gravity === 'xy_center' && resizeConfig.x !== undefined && resizeConfig.y !== undefined) {
				transformations.push(`x_${resizeConfig.x},y_${resizeConfig.y}`);
			}

			if (resizeConfig?.zoom) {
				// Ignore if zoom isn't a number.
				if (isNaN(Number(resizeConfig.zoom))) {
					console.warn('Zoom level must be a number.');
				} else if (Number(resizeConfig.zoom) <= 100) {
					console.warn('Zoom level must be greater than 100.');
				} else {
					let zoom = Number(resizeConfig.zoom) / 100;
					const cropSize = 1 / zoom; // The width and height of the cropped area
					const offset = 1 - cropSize;

					switch (resizeConfig.zoomGravity) {
						case ZoomGravity.NORTH:
							zoomOptimization = `x_${(offset / 2).toFixed(4)},y_0,w_${cropSize.toFixed(4)},h_${cropSize.toFixed(4)},c_crop/`;
							break;
						case ZoomGravity.SOUTH:
							zoomOptimization = `x_${(offset / 2).toFixed(4)},y_${offset.toFixed(4)},w_${cropSize.toFixed(
								4
							)},h_${cropSize.toFixed(4)},c_crop/`;
							break;
						case ZoomGravity.EAST:
							zoomOptimization = `x_${offset.toFixed(4)},y_${(offset / 2).toFixed(4)},w_${cropSize.toFixed(
								4
							)},h_${cropSize.toFixed(4)},c_crop/`;
							break;
						case ZoomGravity.WEST:
							zoomOptimization = `x_0,y_${(offset / 2).toFixed(4)},w_${cropSize.toFixed(4)},h_${cropSize.toFixed(4)},c_crop/`;
							break;
						case ZoomGravity.NORTH_EAST:
							zoomOptimization = `x_${offset.toFixed(4)},y_0,w_${cropSize.toFixed(4)},h_${cropSize.toFixed(4)},c_crop/`;
							break;
						case ZoomGravity.NORTH_WEST:
							zoomOptimization = `x_0,y_0,w_${cropSize.toFixed(4)},h_${cropSize.toFixed(4)},c_crop/`;
							break;
						case ZoomGravity.SOUTH_EAST:
							zoomOptimization = `x_${offset.toFixed(4)},y_${offset.toFixed(4)},w_${cropSize.toFixed(4)},h_${cropSize.toFixed(
								4
							)},c_crop/`;
							break;
						case ZoomGravity.SOUTH_WEST:
							zoomOptimization = `x_0,y_${offset.toFixed(4)},w_${cropSize.toFixed(4)},h_${cropSize.toFixed(4)},c_crop/`;
							break;
						case ZoomGravity.CENTER:
						default:
							const centeredOffset = offset / 2;
							zoomOptimization = `x_${centeredOffset.toFixed(4)},y_${centeredOffset.toFixed(4)},w_${cropSize.toFixed(
								4
							)},h_${cropSize.toFixed(4)},c_crop/`;
							break;
					}
				}
			}

			if (eTrimOptimization?.enabled) {
				let eTrim: string = 'e_trim';
				if (eTrimOptimization.tolerance >= 0 && eTrimOptimization.tolerance <= 100) {
					eTrim += `:${eTrimOptimization.tolerance}`;
				}
				transformations.push(eTrim);
			}
		}

		// Check if targetCrop has actual configuration (not just an empty object)
		// Only process if autoTargetCropEnabled is explicitly true OR manual values exist
		const hasTargetCropConfig = optimizations?.targetCrop && (
			optimizations.targetCrop.autoTargetCropEnabled === true ||
			!String.isEmpty(optimizations.targetCrop.tx) ||
			!String.isEmpty(optimizations.targetCrop.ty) ||
			!String.isEmpty(optimizations.targetCrop.tw) ||
			!String.isEmpty(optimizations.targetCrop.th)
		);

		if (type === 'image' && hasTargetCropConfig) {
			let targetCrop = optimizations.targetCrop;

			// Check if auto target crop is enabled (defaults to true if undefined and no manual values exist)
			const isAutoMode = targetCrop.autoTargetCropEnabled !== false;
			const hasManualValues = !String.isEmpty(targetCrop?.tx) && !String.isEmpty(targetCrop?.ty) &&
									!String.isEmpty(targetCrop?.tw) && !String.isEmpty(targetCrop?.th);

			// Check if subject position is available and valid
			const hasSubjectPosition = hasValidSubjectPosition(optimizations?.subjectPosition);
			let useSubjectPositioning = false;

			if (hasSubjectPosition) {
				// Validate subject position bounds
				const validationErrors = validateSubjectPositionBounds(optimizations.subjectPosition);

				if (validationErrors.length > 0) {
					console.warn('[SUBJECT POSITION] Validation failed, falling back to c_target:', validationErrors);
				} else {
					useSubjectPositioning = true;
					console.log('[SUBJECT POSITION] Using c_subpos transformation', {
						subjectPosition: optimizations.subjectPosition,
						isAutoMode,
						hasManualValues
					});
				}
			}

			// Use c_subpos if subject position is valid, otherwise fall back to c_target
			if (useSubjectPositioning) {
				// Build c_subpos transformation
				targetCropOptimization = this.buildSubposTransformation(
					optimizations.subjectPosition,
					targetCrop,
					resizeConfig,
					isAutoMode,
					hasManualValues,
					targetFilterSegment
				);
				targetCropOptimization += '/';

				console.log('[SUBJECT POSITION] Generated c_subpos transformation:', targetCropOptimization);
			} else {
				// Fall back to existing c_target logic

			if (isAutoMode && !hasManualValues) {
				// Auto mode: use target_auto to let the proxy select the best focal area
				targetCropOptimization = `c_target,target_auto${targetFilterSegment}`;

				// Turn width and height into an aspect ratio for focal area selection
				if (targetCrop.width && targetCrop.height) {
					const gcd = (...arr) => {
						const _gcd = (x, y) => (!y ? x : gcd(y, x % y));
						return [...arr].reduce((a, b) => _gcd(a, b));
					};

					const gcdResult = gcd(Number(targetCrop.width), Number(targetCrop.height));

					targetCropOptimization += `,ar_${Number(targetCrop.width) / gcdResult}:${Number(targetCrop.height) / gcdResult}`;
				} else {
					// If width and height are not provided, use the element width and height
					targetCropOptimization += `,ar_$element.ar`;
				}

				if (targetCrop?.margin) {
					targetCropOptimization += `,${targetCrop.margin}`;
				}

				// Add aspect priority parameter (ap_) - defaults to 'target' if not specified
				if (targetCrop.aspectPriority) {
					targetCropOptimization += `,ap_${targetCrop.aspectPriority}`;
				}

				// Set the mode to be cover if its not already in there.
				if (targetCrop.cropMode === 'contain') {
					targetCropOptimization += ',am_contain';
				} else {
					targetCropOptimization += ',am_cover';
				}
			} else if (hasManualValues) {
				// Manual mode: use specific tx/ty/tw/th coordinates
				targetCropOptimization = `c_target`;
				targetCropOptimization += `,tx_${targetCrop.tx},ty_${targetCrop.ty},tw_${targetCrop.tw},th_${targetCrop.th}`;

				// Turn width and height into an aspect ratio.
				if (targetCrop.width && targetCrop.height) {
					const gcd = (...arr) => {
						const _gcd = (x, y) => (!y ? x : gcd(y, x % y));
						return [...arr].reduce((a, b) => _gcd(a, b));
					};

					const gcdResult = gcd(Number(targetCrop.width), Number(targetCrop.height));

					targetCropOptimization += `,ar_${Number(targetCrop.width) / gcdResult}:${Number(targetCrop.height) / gcdResult}`;
				} else {
					// If width and height are not provided, use the element width and height
					targetCropOptimization += `,ar_$element.ar`;
				}

				if (targetCrop?.margin) {
					targetCropOptimization += `,${targetCrop.margin}`;
				}

				// Add aspect priority parameter (ap_) - defaults to 'target' if not specified
				if (targetCrop.aspectPriority) {
					targetCropOptimization += `,ap_${targetCrop.aspectPriority}`;
				}

				// Set the mode to be cover if its not already in there.
				if (targetCrop.cropMode === 'contain') {
					targetCropOptimization += ',am_contain';
				} else if (targetCropOptimization.indexOf('am_cover') === -1) {
					targetCropOptimization += ',am_cover';
				}
			}

			targetCropOptimization += '/';
			// console.log('Target crop optimization', targetCropOptimization);
			}
		}

		// If there is an advanced string, remove everything else and use it.
		if (optimizations?.advanced) {
			// Remove any starting and ending slashes.
			let advanced = optimizations.advanced.replace(/^\/|\/$/g, '');
			transformations = [advanced];
		}

		// Apply cloudinary optimizations to video and image type layers
		if (type === 'video' || type === 'image') {
			if (qualityOptimization || transformations.length > 0 || targetCropOptimization) {
				console.log('assetPath', assetPath);
				let finalUrl = assetPath;

				if (!assetPath) {
					console.error('Asset path is required for optimization.', assetPath);
					return assetPath;
				}

				if (provider === 'tasker') {
					if (!taskerEngineUrl) {
						console.error('A taskerEngineUrl environment variable is required for tasker optimization.', assetPath);
						return assetPath;
					}

					let joinedTransformations = `${transformations.join(',')}${transformations.length > 0 ? '/' : ''}`;

					// Check if using c_subpos or c_target with auto mode
					const targetCrop = optimizations?.targetCrop;
					const isAutoMode = targetCrop?.autoTargetCropEnabled !== false;
					const hasManualValues = !String.isEmpty(targetCrop?.tx) && !String.isEmpty(targetCrop?.ty) &&
											!String.isEmpty(targetCrop?.tw) && !String.isEmpty(targetCrop?.th);
					const hasAutoTargetCrop = isAutoMode && !hasManualValues &&
											  targetCropOptimization.includes('target_auto');
					const isC_Subpos = targetCropOptimization.includes('c_subpos');
					const hasAutoC_Subpos = isC_Subpos && hasAutoTargetCrop;

					console.log('[AUTO CROP DEBUG]', {
						targetCrop,
						autoTargetCropEnabled: targetCrop?.autoTargetCropEnabled,
						isAutoMode,
						hasManualValues,
						targetCropOptimization,
						hasAutoTargetCrop,
						isC_Subpos,
						hasAutoC_Subpos
					});

					if (hasAutoTargetCrop) {
						// Route through API process endpoint for auto focal area detection
						// This handles both c_target,target_auto and c_subpos,target_auto
						// The processor will load focal areas from DB, select the best match, and forward to Tasker
						const apiBaseUrl = apiUrl || process.env.API_BASE_URL || 'http://localhost:8001';
						finalUrl = `${apiBaseUrl}/asset/process/${targetCropOptimization}${zoomOptimization}${joinedTransformations}${qualityOptimization}path/${assetPath}`;

						// Add cache-busting parameter if seed is provided
						if (seed) {
							finalUrl = CacheBusterUtils.appendCacheBusterToUrl(finalUrl, seed);
						}

						if (isC_Subpos) {
							console.log('[SUBJECT POSITION] Auto c_subpos - routing through API process endpoint:', finalUrl);
						} else {
							console.log('Auto target crop - routing through API process endpoint:', finalUrl);
						}
					} else {
						// Direct to Tasker for manual target crop/c_subpos or no target crop
						finalUrl = `${taskerEngineUrl.replace(
							/\/$/,
							''
						)}/asset/${targetCropOptimization}${zoomOptimization}${joinedTransformations}${qualityOptimization}path/${assetPath}`;

						if (isC_Subpos) {
							console.log('[SUBJECT POSITION] Manual c_subpos - direct to Tasker:', finalUrl);
						}
						// console.log('Final URL', finalUrl);
					}
				} else {
					// Use Cloudinary

					if (!bucketId) {
						console.error('Bucket ID is required for cloudinary optimization.', assetPath);
						return assetPath;
					}
					let joinedTransformations = `${transformations.join(',')}${transformations.length > 0 ? '/' : ''}`;

					// Recognize if the asset path is a cloudinary path.  If it is, don't use the fetch url.
					finalUrl = `https://res.cloudinary.com/${bucketId}/${type}/fetch/${targetCropOptimization}${zoomOptimization}${joinedTransformations}${qualityOptimization}${assetPath}`;

					// Don't use fetch is the asset path is already a cloudinary path.
					if (assetPath && assetPath.includes('res.cloudinary.com')) {
						// Insert the transformations right after the 'upload' part of the image path.
						const parts = assetPath.split('/');
						const uploadIndex = parts.findIndex(part => part === 'upload');

						let transformations = `/${targetCropOptimization}${zoomOptimization}${joinedTransformations}${qualityOptimization}`;
						// Detect if we have a baseline optimization and add it first.
						if (optimizations.baseline) {
							transformations = `bl_${optimizations.baseline}/${transformations}`;
						}

						parts.splice(uploadIndex + 1, 0, transformations);

						finalUrl = parts
							.join('/');
					}
				}

				// Replace any double slashes with a single slash, but not http:// or https://
				finalUrl = finalUrl.replace(/([^:]\/)\/+/g, '$1');

				return finalUrl;
			}
		}

		return assetPath;
	}
}
